import { ElMessage } from 'element-plus';
import { useEmail, type BuildNotifyEmailParams } from '@/composables/useEmail';
import { useQuoteStore } from '@/stores/quoteStore';
import { maintainExpandMode } from './expandMode';
import type { EmissionSuccessProps } from './types';

type QuoteData = ReturnType<typeof useQuoteStore>['quoteData'];

const quoteIdOf = (props: EmissionSuccessProps): number | undefined =>
  props.quotesResponse?.Cobertura?.Id_Cotizacion
    ? Number(props.quotesResponse.Cobertura.Id_Cotizacion)
    : undefined;

const premiumOf = (props: EmissionSuccessProps): number =>
  props.policyData?.primaTotal ?? props.quotesResponse?.Cobertura?.PrimaTotal ?? 0;

const buildEmailRequest = (
  props: EmissionSuccessProps,
  quoteData: QuoteData,
  email: string,
  idCotizacion: number,
): BuildNotifyEmailParams => ({
  idCotizacion,
  email,
  telefono: quoteData.personalData?.whatsapp,
  nombre: quoteData.personalData?.fullName,
  prima: premiumOf(props),
  fechaVigencia: quoteData.cotizacionData?.finVigencia ?? quoteData.cotizacionData?.inicioVigencia,
  quoteData,
});

/** Send the policy or quote documents to the email the user gave. */
export const useEmissionEmail = (props: EmissionSuccessProps, onMissingContact: () => void) => {
  const quoteStore = useQuoteStore();
  const { isLoading: isSendingEmail, sendQuoteEmail } = useEmail();

  const handleSendEmail = async (): Promise<void> => {
    const quoteData = quoteStore.quoteData;
    const email = quoteData.personalData?.email;
    if (!email) {
      ElMessage.warning('Completa tus datos de contacto para enviar los documentos');
      onMissingContact();
      maintainExpandMode();
      return;
    }

    const idCotizacion = quoteIdOf(props);
    if (!idCotizacion) {
      ElMessage.warning('No se puede enviar: ID de cotización no disponible');
      maintainExpandMode();
      return;
    }

    const result = await sendQuoteEmail(buildEmailRequest(props, quoteData, email, idCotizacion));
    if (result.success) {
      ElMessage.success(
        props.allowEmission
          ? 'Documentos de póliza enviados por correo electrónico'
          : 'Cotización enviada por correo electrónico',
      );
    } else {
      ElMessage.error(result.error || 'Error al enviar el email');
    }
    maintainExpandMode();
  };

  return { isSendingEmail, handleSendEmail };
};
